import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { getPillarArticle, getPillarArticles } from '@/data/pillarArticles';

export default function PillarArticlePage() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getPillarArticle(slug, i18n.language) : undefined;
  const allArticles = getPillarArticles(i18n.language);
  const related = allArticles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Guide not found</h1>
          <Link to="/" className="text-green-600 font-semibold hover:text-green-700">
            ← Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-green-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/#pillar-guides" className="hover:text-green-600 transition-colors">{t('pillarSectionTitle')}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-700">{article.title}</span>
          </nav>

          {/* Article Header */}
          <div className="mb-10">
            <div className="aspect-video rounded-2xl overflow-hidden mb-8 shadow-lg">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{article.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <span>{t('articleAuthor')}</span>
              <span>•</span>
              <span>{t('articleMeta')}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['CSSBuy Spreadsheet', 'CSSBuy Guide', 'CSSBuy Review'].map((tag) => (
                <span key={tag} className="px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Quick Answer / Excerpt */}
          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg mb-10">
            <p className="text-gray-700 text-sm leading-relaxed">{article.excerpt}</p>
          </div>

          {/* Intro */}
          <div className="prose prose-lg max-w-none mb-8">
            <div className="text-gray-700 leading-relaxed space-y-4">
              {article.intro.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-12 mb-12">
            {article.sections.map((section, i) => (
              <section key={i}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-700 leading-relaxed space-y-4">
                  {section.paragraphs.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('articleFAQ')}</h2>
            <div className="space-y-4">
              {article.faq.map((item, i) => (
                <div key={i} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.q}</h3>
                  <p className="text-gray-600 text-sm">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Internal Link to Home */}
          <div className="mt-10 bg-green-50 rounded-xl p-6 border border-green-100">
            <p className="text-green-800 text-sm font-medium mb-2">{t('linkSpreadsheet')}</p>
            <p className="text-green-700 text-sm leading-relaxed mb-4">
              {t('whatIsDesc')}
            </p>
            <Link
              to="/"
              className="inline-flex items-center text-green-600 font-semibold hover:text-green-700 transition-colors"
            >
              {t('articleBackToHome')}
            </Link>
          </div>

          {/* Related Articles */}
          <div className="mt-10">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{t('relatedArticles')}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((a) => (
                <Link
                  key={a.slug}
                  to={`/article/${a.slug}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md hover:border-green-200 transition-all"
                >
                  <div className="aspect-video overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">{a.title}</h4>
                    <p className="text-xs text-gray-600 line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-gray-900 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-white mb-4">{t('finalCtaTitle')}</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
              >
                {t('finalCtaPrimary')}
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30 whitespace-nowrap"
              >
                {t('finalCtaSecondary')}
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                {t('cssbuySpreadsheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                {t('buyItNow')}
              </a>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 text-center text-xs text-gray-500">
            {t('heroDisclaimer')}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}